import { useState } from 'react';
import _ from 'lodash';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import styled from 'styled-components';
import { getColor } from 'common/constants/_colors';
import SearchInput from 'components/SearchInput';
import useCheckboxListToggle from 'components/hooks/useCheckboxListToggle';
import SelectableItem from './SelectableItem';
import { pluralize } from 'common/utils/utils';
import { PM_PROVIDERS } from 'common/constants/constants';
import { useSelector } from 'react-redux';
import { pmToolSensorIdSelector } from 'state/selectors/sensors-authorizations';
import useSettingsService from 'components/hooks/useSettingsService';
import { SettingValues } from '@linearb/linjs-common';
import { INCIDENTS_STRATEGIES } from '../../../../settings/views/Settings/components/IncidentDetectionSetting/incidents-detection-settings.constants';
import { LBButton } from '@linearb/buzz';

const WIDGET_CATEGORIES = {
  PROJECT_MANAGEMENT: 'Project Management'
};

const MetricsContainer = styled(Flex)`
  height: 40rem;
  background-color: ${getColor('catskillGray')};
  overflow-y: auto;
  align-content: start;
  border-top: 1px solid ${getColor('botticelli')};
  border-bottom: 1px solid ${getColor('botticelli')};
`;

const SelectMetricsModal = ({
  metrics = [],
  searchValue: _searchValue = '',
  onDone,
  isDisableBtn = () => false,
  shouldDisableSelection = () => false,
  instructions = '',
  initialState = []
}) => {
  const { getSettingOrDefinitionValue } = useSettingsService();
  const [selectedMetrics, toggleSelectedMetric] =
    useCheckboxListToggle(initialState);
  const [searchValue, setSearchValue] = useState(_searchValue);
  const [clicked, setClicked] = useState(false);
  const pmProvider = useSelector(pmToolSensorIdSelector);
  const incidentsStrategy = getSettingOrDefinitionValue({
    name: SettingValues.INCIDENTS_STRATEGY
  });

  const isAzureBoards = pmProvider === PM_PROVIDERS.AZURE_BOARDS;
  const isReportedIncidentsStrategy =
    incidentsStrategy?.key === INCIDENTS_STRATEGIES.REPORTED;

  const disableButton = isDisableBtn(selectedMetrics);
  const disableSelection = shouldDisableSelection(selectedMetrics);

  const getOmitCategories = () => {
    const omitCategories = [];
    if (isAzureBoards && !isReportedIncidentsStrategy)
      omitCategories.push(WIDGET_CATEGORIES.PROJECT_MANAGEMENT);

    return omitCategories;
  };

  const shouldHideWidget = widget => {
    switch (widget.id) {
      case 22: //issues done
      case 24: //story points
        if (isAzureBoards && isReportedIncidentsStrategy) return true;
        return false;
      default:
        return false;
    }
  };

  const filteredMetrics = metrics.filter(({ name, description }) => {
    const formattedInput = input => input.trim().toLowerCase();
    const formattedSearchValue = formattedInput(searchValue);
    return (
      formattedInput(name).includes(formattedSearchValue) ||
      formattedInput(description).includes(formattedSearchValue)
    );
  });

  const categorisedMetrics = _.omit(
    filteredMetrics.reduce((metricsByCategory, metric) => {
      if (shouldHideWidget(metric)) return metricsByCategory;

      if (metric.category in metricsByCategory) {
        metricsByCategory[metric.category].push(metric);
      } else {
        metricsByCategory[metric.category] = [metric];
      }
      return metricsByCategory;
    }, {}),
    getOmitCategories()
  );

  return (
    <Flex direction='column' padding='medium' justify='space-between'>
      <Flex mt='big' pb='small' justify='space-between' items='flex-end'>
        <Text size='x-x-large' weight='300'>
          Select metrics:
        </Text>
        <SearchInput
          height='2.5rem'
          width='30%'
          variant='outlined'
          onChange={value => setSearchValue(value)}
          value={searchValue}
          placeholder='Search by name...'
        />
      </Flex>

      <MetricsContainer direction='column' padding='small'>
        {Object.keys(categorisedMetrics).map(key => (
          <Flex padding='x-small' direction='column' key={key}>
            <Text mb='0.3rem'>{key.toUpperCase()}</Text>
            {categorisedMetrics[key].map(({ name, description }) => (
              <SelectableItem
                key={name}
                title={name}
                description={description}
                checked={selectedMetrics.has(name)}
                toggleSelection={() => {
                  toggleSelectedMetric(name);
                }}
                disabled={disableSelection}
              />
            ))}
          </Flex>
        ))}
      </MetricsContainer>
      <Flex pt='small' justify='space-between' items='center'>
        <Flex direction='column'>
          {disableButton && (
            <Text size='medium' width='32rem' pb='0.5rem' pt='0.2rem'>
              {instructions}
            </Text>
          )}
          <Text size='medium' width='32rem'>
            {pluralize(selectedMetrics.size, 'Metric')} Selected
          </Text>
        </Flex>

        <LBButton
          disabled={disableButton || clicked}
          data-cy='select-metrics-done-button'
          onClick={() => {
            onDone(selectedMetrics);
            setClicked(true);
          }}
        >
          Done
        </LBButton>
      </Flex>
    </Flex>
  );
};

export default SelectMetricsModal;
